import { Select } from "../../../utils/local-antd";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/browser";
import styled from "styled-components";

import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { Modal } from "../../../components/modal/Modal";
import { CreateEntryDto } from "../../../model/entry/dto/CreateNewEntryDto";
import { FormInput } from "../../../model/form/FormInput";
import { InputTypes } from "../../../model/form/types/InputTypes.enum";
import { entryActions } from "../../../redux/entry/entry.slice";
import { footerActions } from "../../../redux/footer-daily-stats/footer-daily-stats.slice";
import { formSliceSelectors } from "../../../redux/forms/form.selector";
import { formActions } from "../../../redux/forms/form.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { TestIDs } from "../../../tests/types/testIds";
import { Spacing } from "../../../theme/Spacing";
import { AtiraToast } from "../../../utils/AtiraToast";
import { onSetTypedCRUDErrors } from "../../../utils/onSetTypedCRUDErrors";
import { getRenderedFieldByType } from "../utils/getRenderedFieldByType";
import { useDrawersContext } from "../../tasks/drawers-context";
import { entrySliceSelectors } from "../../../redux/entry/entry.selector";
import { EntriesTableMainFields } from "../../../model/entries-table-settings/types/EntriesTableMainFields.enum";
import { FormType } from "../../../model/form/types/FormType.enum";
import { useLocation } from "react-router-dom";
import { useAtiraFormContext } from "../../form/context/form-context";
import isFunction from "lodash/isFunction";
import { Info } from "../../../components/Info";

const SaveButton = styled(Button)`
  width: 7rem;
  height: 3rem;
  margin: 1rem auto;
  font-size: 1.3rem;
`;

const Container = styled(Flex)`
  flex-direction: column;
  justify-content: center;
  margin: auto;
  gap: ${Spacing.m};
  width: 100%;
`;

export const EntryCreateModal: React.FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const { selectedForm, setSelectedForm } = useAtiraFormContext();
  const {
    entryCreateVisible,
    setEntryCreateVisible,
    entryCreateAction,
    setEntryCreateAction,
  } = useDrawersContext();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const allForms = useAppSelector(formSliceSelectors.selectForms);
  const forms = [...allForms]?.filter((form) => form.type === FormType.ENTRY)!;

  // `selectedForm` lives in a context that outlives the forms list it was picked
  // from - e.g. after switching to another account the previous account's form
  // is still held here. Only trust it while it matches a form the current
  // account actually owns; otherwise fall back to the default form.
  const selectedFormIsValid =
    !!selectedForm && forms.some((form) => form._id === selectedForm._id);

  const currentForm =
    (selectedFormIsValid ? selectedForm : undefined) ||
    forms.find((form) => form.orphan) ||
    forms[0];

  useEffect(() => {
    if (selectedForm && forms.length && !selectedFormIsValid) {
      setSelectedForm(null);
    }
  }, [selectedForm, selectedFormIsValid, forms.length, setSelectedForm]);
  const mainFields = useAppSelector(
    entrySliceSelectors.selectUserEntryTableSettings,
  )?.mainFields;

  const [createEntryLoading, setCreateEntryLoading] = useState(false);

  const formMethods = useForm<CreateEntryDto>({
    defaultValues: {
      formId: undefined,
      ownerId: undefined,
      origin: undefined,
      orphan: undefined,
      ...Object.fromEntries(
        currentForm?.inputs?.map((inp) => [inp.name, undefined]) ?? [],
      ),
    },
  });
  const { control, getValues, handleSubmit, reset, setError } = formMethods;

  const currentFormInputs = currentForm?.inputs?.filter(
    (input) => input.hidden !== true,
  );

  const withUncheckedBoxesAsFalse = (dto: CreateEntryDto) => {
    currentFormInputs?.forEach((input) => {
      if (input.type.toLowerCase() === InputTypes.CHECKBOX.toLowerCase()) {
        dto[input.name!] = dto[input.name!] === true;
      }
    });

    return dto;
  };

  const onFormSelectChange = (formId: string) => {
    const _form = forms.find((f) => f._id === formId)!;

    if (_form) {
      setSelectedForm(_form);
      reset();
    }
  };

  const localOnClose = () => {
    setEntryCreateVisible(false);
    reset();
    if (isFunction(entryCreateAction)) {
      setEntryCreateAction(null);
    }
  };

  const onEntrySubmit = async () => {
    try {
      setCreateEntryLoading(true);

      const isOnFolderFormsPage = pathname.includes("/forms/folder-");

      const dto = withUncheckedBoxesAsFalse(getValues());

      currentFormInputs?.forEach((input) => {
        if (
          input.type.toLowerCase() === InputTypes.DATE.toLowerCase() &&
          dto[input.name!]
        ) {
          dto[input.name!] = new Date(dto[input.name!] as string | Date);
        }
      });

      await dispatch(
        entryActions.createNewEntry({
          ...dto,
          formId: currentForm?._id!,
          orphan: currentForm?.orphan === true,
          ownerId: userId,
          origin: `Manual Entry${!currentForm?.orphan ? ` - ${currentForm?.title}` : ""}`,
        } as CreateEntryDto),
      ).unwrap();

      await Promise.all([
        dispatch(
          entryActions.getLatestEntries({
            userId,
            meta: { count: 10, page: 0 },
          }),
        ).unwrap(),
        dispatch(formActions.getForms({ userId })),
        ...(isOnFolderFormsPage
          ? [dispatch(formActions.getFolders({ userId }))]
          : []),
        dispatch(footerActions.getFooterDailyStats({ userId })),
      ]);

      localOnClose();

      AtiraToast.success(t("entries.entry.create.success"));
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
      onSetTypedCRUDErrors(e, setError);
      Sentry.captureException(e, {
        tags: { location: "EntryCreateModal" },
        user: { id: userId },
      });
    } finally {
      setCreateEntryLoading(false);
    }
  };

  const onSave = () => {
    if (isFunction(entryCreateAction)) {
      handleSubmit((dto) => {
        entryCreateAction({
          ...withUncheckedBoxesAsFalse(dto),
          formId: currentForm?._id!,
          orphan: currentForm?.orphan === true,
          ownerId: userId,
          origin: `Manual Entry${!currentForm?.orphan ? ` - ${currentForm?.title}` : ""}`,
        } as CreateEntryDto);
        reset();
      })();
    } else {
      handleSubmit(onEntrySubmit)();
    }
  };

  return (
    <Modal
      open={entryCreateVisible}
      onClose={localOnClose}
      title={t("entries.new_entry")}
      content={{ maxHeight: "80vh" }}
      key={currentForm?._id}
      destroyOnHidden
      className={TestIDs.entries.createEdit.modal.wrapper}
    >
      <Container>
        {currentForm?.orphan ? (
          <Text color="gray" align="center">
            {t("entries.create.entry_modal.without_form_note")}
          </Text>
        ) : null}

        {currentForm?.title && currentForm.orphan !== true ? (
          <Info
            info={t("entries.create_entry.modal.form_title", {
              title: currentForm.title,
            })}
          />
        ) : null}

        {forms.length > 1 ? (
          <Flex flexDirection="column" gap="s">
            <Select
              defaultValue={
                currentForm?.orphan
                  ? t("forms.manual_entry")
                  : currentForm?.title
              }
              placeholder={t("common.dropdown.select")}
              options={forms.map((f) => ({
                value: f._id,
                label: f.orphan ? t("forms.manual_entry") : f.title,
              }))}
              onChange={onFormSelectChange}
              style={{ height: "3rem", width: "100%" }}
            />
          </Flex>
        ) : null}

        {currentFormInputs?.length ? (
          <Flex flexDirection="column" gap="m">
            {currentFormInputs.map((input: Partial<FormInput>) => (
              <Controller
                name={input.name!}
                control={control}
                rules={{
                  required: {
                    value:
                      Boolean(
                        mainFields?.includes(
                          input.name as EntriesTableMainFields,
                        ),
                      ) ||
                      input.settings?.required ||
                      false,
                    message: t("common.this_field_required"),
                  },
                  maxLength: input.settings?.maxChar,
                }}
                render={({ field, fieldState }) =>
                  getRenderedFieldByType(
                    input,
                    field,
                    fieldState,
                    currentForm?.orphan
                      ? t(`common.${input.name}`, { defaultValue: input.label })
                      : input.label,
                    Boolean(
                      mainFields?.includes(
                        input.name as EntriesTableMainFields,
                      ),
                    ) || input.settings?.required,
                    { possibleBookedSlots: currentForm?._possibleBookedSlots },
                  )
                }
                key={input._id}
              />
            ))}
          </Flex>
        ) : null}

        <SaveButton
          dataTestId={TestIDs.entries.createEdit.form.submitButton}
          onClick={onSave}
          loading={createEntryLoading}
        >
          {t("common.save")}
        </SaveButton>
      </Container>
    </Modal>
  );
};
